import { NgClass } from "@angular/common";
import { RouterModule } from "@angular/router";
import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	ViewEncapsulation,
	computed,
	effect,
	inject,
	input,
	signal,
} from "@angular/core";
import { SvgIconComponent } from "angular-svg-icon";
import { MenuItem } from "primeng/api";
import { AvatarModule } from "primeng/avatar";
import { MenuModule } from "primeng/menu";
import { EntitledMenuItem } from "../../models/entitledMenuItem";
import { InternalOrder } from "../../models/internal-orders";
import { UsersPrivilegeKey } from "../../models/user";
import { AuthService } from "../../services/auth/auth.service";
import { OrdersService } from "../../services/orders/orders.service";
import { StorefrontService } from "../../services/storefront/storefront.service";
import { UserService } from "../../services/user/user.service";
import { SharedService } from "../../services/shared/shared.service";
@Component({
    selector: "app-sidenav",
    imports: [MenuModule, AvatarModule, SvgIconComponent, NgClass, RouterModule],
    templateUrl: "./sidenav.component.html",
    styleUrl: "./sidenav.component.scss",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavComponent implements OnInit {
	userService = inject(UserService);
	storeService = inject(StorefrontService);
	sharedService = inject(SharedService);
	ordersService = inject(OrdersService);
	authService = inject(AuthService);
	isUserLoggedIn = signal(this.userService.loggedInUser() !== undefined);
	clickedParentItem = signal<MenuItem | undefined>(undefined);
	activeParentItem = signal<MenuItem | undefined>(undefined);
	storeId = "";
	isOpen = signal(false);
	hovered = signal(false);
	dashboard = signal($localize`:@@nav.side.dashboard:Dashboard`);
	orders = signal($localize`:@@nav.side.orders:Orders`);
	liveOrders = signal($localize`:@@nav.side.live-orders:Live`);
	catalogs = signal($localize`:@@nav.side.catalogs:Catalog`);
	users = signal($localize`:@@nav.side.users:Users`);
	accounts = signal($localize`:@@nav.side.accounts:Accounts`);
	schedules = signal($localize`:@@nav.side.schedules:Schedules`);
	collapse = signal($localize`:@@nav.side.collapse:Collapse`);
	templates = signal($localize`:@@nav.side.templates:Templates`);
	storeFront = signal($localize`:@@nav.side.storefront:Storefront`);
	styling = signal($localize`:@@nav.side.styling:Styling`);
	testOrders = signal($localize`:@@nav.side.test-orders:Test`);
	internalOrders = signal($localize`:@@nav.side.internal-orders:Internal`);
	
	configuration = signal($localize`:@@nav.side.configuration:Configuration`);
	supportSetup = signal($localize`:@@nav.side.support.setup:Support Setup`);
	promotions = signal($localize`:@@nav.side.promotion:Promotions`);
	settings = signal($localize`:@@nav.side.settings:Settings`);
	programSettings = signal(
		$localize`:@@nav.side.program.settings:Program Settings`,
	);
	help = signal($localize`:@@nav.side.help:Help`);
	brands = signal($localize`:@@nav.side.brands:Brands`);
	upperMenuItems = computed<EntitledMenuItem[]>(() => {
		if (!this.userService.loggedInUser()) {
			return [];
		} else {
			let ordersChildMenu = [{
							label: this.orders(),
							id: "orders",
							routerLink: "/orders",
							routerLinkActiveOptions: { exact: true },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Orders,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Orders,
							),
						},
						{
							label: this.testOrders(),
							id: "test-orders",
							routerLink: 'test-orders',
							routerLinkActiveOptions: { exact: false },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Orders,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Orders,
							),
						},
						{
							label: this.internalOrders(),
							id: "internal-orders",
							routerLink: 'internal-orders',
							routerLinkActiveOptions: { exact: false },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Orders,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Orders,
							),
						}
					];
			return [
				{
					label: this.dashboard(),
					id: "dashboard",
					routerLink: "/dashboard",
					styleClass: "",
					icon: "/assets/icons/dashboard.svg",
					routerLinkActiveOptions: { exact: true },
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Dashboard,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Dashboard,
					),
				},
				{
					separator: true,
				},
				// {
				// 	label: this.orders(),
				// 	id: "orders",
				// 	routerLink: "/orders",
				// 	styleClass: "",
				// 	icon: "/assets/icons/receipt.svg",
				// 	disabled: this.userService.isReadOnlyAccess(
				// 		UsersPrivilegeKey.Orders,
				// 	),
				// 	visible: !this.userService.isAccessNone(
				// 		UsersPrivilegeKey.Orders,
				// 	),
				// },
				{
					label: this.orders(),
					id: "orders",
					expanded:
						this.clickedParentItem()?.id === "orders" ? true : false,
					active: this.activeParentItem()?.id === "orders" ? true : false,
					routerLink: "orders",
					styleClass: "p-menuitem",
					icon: "/assets/icons/receipt.svg",
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Orders,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Orders,
					),
					items: this.userService.isAccessNone(
								UsersPrivilegeKey.Orders_Test_Orders) ? [] : [
						{
							label: this.liveOrders(),
							id: "live-orders",
							routerLink: "/orders",
							routerLinkActiveOptions: { exact: true },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Orders,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Orders,
							),
						},
						{
							label: this.testOrders(),
							id: "test-orders",
							routerLink: 'test-orders',
							routerLinkActiveOptions: { exact: false },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Orders_Test_Orders,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Orders_Test_Orders,
							),
						},
						{
							label: this.internalOrders(),
							id: "internal-orders",
							routerLink: 'internal-orders',
							routerLinkActiveOptions: { exact: false },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Orders_Internal_Ordering,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Orders_Internal_Ordering,
							),
							new: !this.ordersService.hasInternalOrdering(),
						}
					],
				},				
				{
					label: this.catalogs(),
					id: "catalogs",
					routerLink: "catalog",
					styleClass: "",
					icon: "/assets/icons/gift-card.svg",
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Catalog,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Catalog,
					),
				},
				{
					label: this.promotions(),
					id: "promotion",
					routerLink: "promotions",
					styleClass: "",
					icon: "/assets/icons/promotion.svg",
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Promotions,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Promotions,
					),
				},
				{
					separator: true,
				},
				{
					label: this.users(),
					id: "users",
					routerLink: "users",
					styleClass: "",
					icon: "/assets/icons/users.svg",
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Users,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Users,
					),
				},
				{
					label: this.accounts(),
					id: "accounts",
					routerLink: "accounts",
					styleClass: "",
					icon: "/assets/icons/account.svg",
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Accounts,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Accounts,
					),
				},
				{
					label: this.schedules(),
					id: "schedules",
					routerLink: "schedules",
					styleClass: "",
					icon: "/assets/icons/settings-two.svg",
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Schedule,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Schedule,
					),
				},
				{
					separator: true,
					visible:
						!this.userService.isAccessNone(
							UsersPrivilegeKey.Schedule,
						) &&
						!this.userService.isAccessNone(
							UsersPrivilegeKey.Accounts,
						) &&
						!this.userService.isAccessNone(UsersPrivilegeKey.Users),
				},
				{
					label: this.storeFront(),
					id: "storefront",
					expanded:
						this.clickedParentItem()?.id === "storefront" ? true : false,
					active: this.activeParentItem()?.id === "storefront" ? true : false,
					routerLink: "storefront",
					styleClass: "p-menuitem",
					icon: "/assets/icons/storefront.svg",
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Stores,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Stores,
					),
					items: [
						{
							label: this.configuration(),
							id: "configuration",
							routerLink: "storefront/configuration",
							routerLinkActiveOptions: { exact: true },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Stores,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Stores,
							),
						},
						{
							label: this.styling(),
							id: "styling",
							routerLink: `storefront/style/${this.storeId}`,
							routerLinkActiveOptions: { exact: false },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Stores_styler,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Stores_styler,
							),
						},
						// { label: this.supportSetup(), routerLinkActiveOptions: { exact: false } },
					],
				},
				{
					label: this.programSettings(),
					id: "programSettings",
					expanded:
						this.clickedParentItem()?.id === "programSettings" ? true : false,
					active:
						this.activeParentItem()?.id === "programSettings" ? true : false,
					routerLink: "program-settings",
					icon: "/assets/icons/settings-one.svg",
					disabled: this.userService.isReadOnlyAccess(
						UsersPrivilegeKey.Templates,
					),
					visible: !this.userService.isAccessNone(
						UsersPrivilegeKey.Templates,
					),
					items: [
						{
							label: this.templates(),
							id: "templates",
							routerLink: "program-settings/templates",
							routerLinkActiveOptions: { exact: true },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Templates,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Templates,
							),
						},
						{
							label: this.brands(),
							id: "brands",
							routerLink: "program-settings/brands",
							routerLinkActiveOptions: { exact: true },
							disabled: this.userService.isReadOnlyAccess(
								UsersPrivilegeKey.Brand,
							),
							visible: !this.userService.isAccessNone(
								UsersPrivilegeKey.Brand,
							),
						},
					],
				},
			];
		}
	});

	ngOnInit(): void {
		if (this.isUserLoggedIn()) {
			if (!this.userService.isAccessNone(UsersPrivilegeKey.Stores_styler)) {
				this.getStore();
			}
			if (!this.userService.isAccessNone(UsersPrivilegeKey.Orders_Internal_Ordering)) {
				this.checkHasInternalOrdering();
			}
		}
	}
	checkHasInternalOrdering() {
		this.ordersService.getInternalOrdering(this.authService.getLowMerchantId()).subscribe({
			next: (data) => this.ordersService.setHasInternalOrdering(((data as InternalOrder[]) ?? []).length > 0),
			error: () => this.ordersService.setHasInternalOrdering(false),
		});
	}
	getStore() {
		this.storeService.getStoreConfig().subscribe((data: any) => {
			if (data.length) {
				this.storeId = data[0].storeId;
			}
		});
	}
	lowerMenuItems = computed<MenuItem[]>(() => {
		const collapseLabel = $localize`:@@sidenav.collapse:Collapse`;
		const expandLabel = $localize`:@@sidenav.expand:Expand`;
		return [
			{
				label: this.settings(),
				id: "settings",
				routerLink: "settings/general",
				styleClass: "",
				icon: "/assets/icons/settings-adjust.svg",
				disabled: this.userService.isReadOnlyAccess(
					UsersPrivilegeKey.Settings,
				),
				visible: !this.userService.isAccessNone(
					UsersPrivilegeKey.Settings,
				),
			},			
			{
				label: this.help(),
				id: "help",
				routerLink: "help-center",
				styleClass: "",
				icon: "/assets/icons/help.svg",
				disabled: this.userService.isReadOnlyAccess(
					UsersPrivilegeKey.Help_Center,
				),
				visible: !this.userService.isAccessNone(
					UsersPrivilegeKey.Help_Center,
				),
			},
			{
				label: this.isOpen() ? collapseLabel : expandLabel,
				icon: this.isOpen()
					? "/assets/icons/sidebar-collapse.svg"
					: "/assets/icons/sidebar-expand.svg",
				command: () => this.toggle(),
				visible: true,
			},
		];
	});

	toggle() {
		this.isOpen.update((val) => !val);
		this.sharedService.setCollapse(this.isOpen());
	}

	onParentItemClick(item: MenuItem | undefined) {
		if (item?.id === this.clickedParentItem()?.id) {
			this.clickedParentItem.set(undefined);
		} else {
			this.clickedParentItem.set(item);
		}
	}

	onActiveParentItemClick(item: MenuItem | undefined) {
		this.activeParentItem.set(item);
	}

	protected readonly UsersPrivilegeKeys = UsersPrivilegeKey;
}
